/**
 * Enhanced Color Contrast Validator
 * 
 * Comprehensive color contrast validation system that ensures accessibility
 * compliance and optimal visual readability for caption and text-overlay systems.
 * 
 * Features:
 * - WCAG 2.1 AA and AAA compliance checking
 * - Automatic color correction suggestions
 * - Context-aware contrast validation
 * - Support for various background types
 * - Professional color palette generation
 */

import { BackgroundType, OutlineType, TextAnimationType } from './types';

/**
 * WCAG contrast ratio standards
 */
export const CONTRAST_STANDARDS = {
    AA_NORMAL: 4.5,      // WCAG AA for normal text
    AA_LARGE: 3.0,       // WCAG AA for large text (18pt+ or 14pt+ bold)
    AAA_NORMAL: 7.0,     // WCAG AAA for normal text
    AAA_LARGE: 4.5,      // WCAG AAA for large text
    MINIMAL: 2.0         // Minimal readable contrast
} as const;

/**
 * Color names to hex mapping (expanded)
 */
const COLOR_NAME_MAP: Record<string, string> = {
    // Basic colors
    black: "#000000", white: "#FFFFFF", red: "#FF0000", green: "#008000",
    blue: "#0000FF", yellow: "#FFFF00", cyan: "#00FFFF", magenta: "#FF00FF",
    gray: "#808080", orange: "#FFA500", purple: "#800080", pink: "#FFC0CB",
    brown: "#A52A2A",
    
    // Extended palette for better suggestions
    darkblue: "#000080", lightblue: "#ADD8E6", darkgreen: "#006400",
    lightgreen: "#90EE90", darkred: "#8B0000", lightgray: "#D3D3D3",
    darkgray: "#A9A9A9", navy: "#000080", maroon: "#800000", 
    olive: "#808000", lime: "#00FF00", aqua: "#00FFFF", 
    teal: "#008080", silver: "#C0C0C0", fuchsia: "#FF00FF"
} as const;

/**
 * Enhanced color name to hex conversion
 */
export function colorNameToHex(color: string): string {
    if (!color) return "#000000";
    
    // Handle various input formats
    const normalizedColor = color.toLowerCase().trim();
    
    // Direct hex color
    if (normalizedColor.startsWith('#')) {
        return normalizedColor;
    }
    
    // RGB/RGBA format - extract hex equivalent (simplified)
    if (normalizedColor.startsWith('rgb')) {
        const match = normalizedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
            const r = parseInt(match[1]).toString(16).padStart(2, '0');
            const g = parseInt(match[2]).toString(16).padStart(2, '0');
            const b = parseInt(match[3]).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        }
        return "#000000"; // Fallback for invalid format
    }
    
    // Color name lookup
    return COLOR_NAME_MAP[normalizedColor] || color;
}

/**
 * Enhanced RGB to luminance calculation (WCAG compliant)
 */
export function rgbToLuminance(r: number, g: number, b: number): number {
    // Normalize RGB values to 0-1 range
    const normalize = (value: number) => {
        const normalized = value / 255;
        return normalized <= 0.03928 
            ? normalized / 12.92 
            : Math.pow((normalized + 0.055) / 1.055, 2.4);
    };

    const rLum = normalize(r);
    const gLum = normalize(g);
    const bLum = normalize(b);

    // WCAG luminance formula
    return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
}

/**
 * Calculate WCAG compliant contrast ratio
 */
export function calculateContrastRatio(luminance1: number, luminance2: number): number {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Convert hex color to RGB values
 */
export function hexToRgb(hex: string): [number, number, number] {
    const normalizedHex = hex.startsWith("#") ? hex.slice(1) : hex;
    
    // Handle 3-character hex codes
    let fullHex = normalizedHex;
    if (normalizedHex.length === 3) {
        fullHex = normalizedHex[0] + normalizedHex[0] + 
                 normalizedHex[1] + normalizedHex[1] + 
                 normalizedHex[2] + normalizedHex[2];
    }
    
    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);
    
    return [r, g, b];
}

/**
 * Get luminance for a color
 */
export function getColorLuminance(color: string): number {
    const hex = colorNameToHex(color);
    const [r, g, b] = hexToRgb(hex);
    return rgbToLuminance(r, g, b);
}

/**
 * Enhanced contrast validation result
 */
export interface ContrastValidationResult {
    sufficientContrast: boolean;
    contrastRatio: number;
    wcagLevel: 'FAIL' | 'AA' | 'AAA';
    replacementColor?: string;
    suggestion?: string;
    confidence: 'low' | 'medium' | 'high';
}

/**
 * Context for contrast validation
 */
export interface ContrastContext {
    fontSize: number;
    isBold: boolean;
    hasOutline: boolean;
    backgroundType: BackgroundType;
    textAnimation: TextAnimationType;
    isActiveWord: boolean;
}

/**
 * Enhanced contrast validation with context awareness
 */
export function validateContrast(
    foregroundColor: string,
    backgroundColor: string,
    context: Partial<ContrastContext> = {},
    targetStandard: keyof typeof CONTRAST_STANDARDS = 'AA_NORMAL'
): ContrastValidationResult {
    const fgHex = colorNameToHex(foregroundColor);
    const bgHex = colorNameToHex(backgroundColor);
    
    const fgLuminance = getColorLuminance(fgHex);
    const bgLuminance = getColorLuminance(bgHex);
    const contrastRatio = calculateContrastRatio(fgLuminance, bgLuminance);
    
    // Determine appropriate standard based on context
    let requiredRatio = CONTRAST_STANDARDS[targetStandard];
    
    // Adjust for large text (18pt+ or 14pt+ bold)
    const isLargeText = (context.fontSize && context.fontSize >= 18) || 
                       (context.fontSize && context.fontSize >= 14 && context.isBold);
    
    if (isLargeText && targetStandard === 'AA_NORMAL') {
        requiredRatio = CONTRAST_STANDARDS.AA_LARGE;
    } else if (isLargeText && targetStandard === 'AAA_NORMAL') {
        requiredRatio = CONTRAST_STANDARDS.AAA_LARGE;
    }
    
    // Special handling for outlined text (can use slightly lower contrast)
    if (context.hasOutline && requiredRatio > CONTRAST_STANDARDS.AA_LARGE) {
        requiredRatio = CONTRAST_STANDARDS.AA_LARGE;
    }
    
    const sufficientContrast = contrastRatio >= requiredRatio;
    
    // Determine WCAG level achieved
    let wcagLevel: 'FAIL' | 'AA' | 'AAA' = 'FAIL';
    const aaThreshold = isLargeText ? CONTRAST_STANDARDS.AA_LARGE : CONTRAST_STANDARDS.AA_NORMAL;
    const aaaThreshold = isLargeText ? CONTRAST_STANDARDS.AAA_LARGE : CONTRAST_STANDARDS.AAA_NORMAL;
    
    if (contrastRatio >= aaaThreshold) {
        wcagLevel = 'AAA';
    } else if (contrastRatio >= aaThreshold) {
        wcagLevel = 'AA';
    }
    
    let replacementColor: string | undefined;
    let suggestion: string | undefined;
    let confidence: 'low' | 'medium' | 'high' = 'high';
    
    if (!sufficientContrast) {
        // Generate replacement color suggestion
        const { color: suggestedColor, confidence: suggestedConfidence } = getSuggestedContrastColor(
            backgroundColor, 
            requiredRatio,
            context
        );
        replacementColor = suggestedColor;
        confidence = suggestedConfidence;
        
        suggestion = generateContrastSuggestion(contrastRatio, requiredRatio, context);
    }
    
    return {
        sufficientContrast,
        contrastRatio,
        wcagLevel,
        replacementColor,
        suggestion,
        confidence
    };
}

/**
 * Get suggested color for better contrast
 */
function getSuggestedContrastColor(
    backgroundColor: string,
    requiredRatio: number,
    context: Partial<ContrastContext> = {}
): { color: string; confidence: 'low' | 'medium' | 'high' } {
    const bgLuminance = getColorLuminance(backgroundColor);
    
    // Try black and white first (most common solutions)
    const blackContrast = calculateContrastRatio(bgLuminance, 0);
    const whiteContrast = calculateContrastRatio(bgLuminance, 1);
    
    if (blackContrast >= requiredRatio) {
        return { color: "#000000", confidence: 'high' };
    }
    
    if (whiteContrast >= requiredRatio) {
        return { color: "#FFFFFF", confidence: 'high' };
    }
    
    // Generate custom color based on background
    const isLightBackground = bgLuminance > 0.5;
    
    if (isLightBackground) {
        // For light backgrounds, try darker colors
        const darkColors = ["#333333", "#444444", "#555555", "#666666", "#1a1a1a", "#2c2c2c"];
        for (const color of darkColors) {
            const contrast = calculateContrastRatio(bgLuminance, getColorLuminance(color));
            if (contrast >= requiredRatio) {
                return { color, confidence: 'medium' };
            }
        }
        return { color: "#000000", confidence: 'low' };
    } else {
        // For dark backgrounds, try lighter colors
        const lightColors = ["#cccccc", "#bbbbbb", "#aaaaaa", "#999999", "#e5e5e5", "#d3d3d3"];
        for (const color of lightColors) {
            const contrast = calculateContrastRatio(bgLuminance, getColorLuminance(color));
            if (contrast >= requiredRatio) {
                return { color, confidence: 'medium' };
            }
        }
        return { color: "#FFFFFF", confidence: 'low' };
    }
}

/**
 * Generate helpful suggestion text
 */
function generateContrastSuggestion(
    currentRatio: number,
    requiredRatio: number,
    context: Partial<ContrastContext> = {}
): string {
    const shortfall = requiredRatio - currentRatio;
    const shortfallPercent = Math.round((shortfall / requiredRatio) * 100);
    
    let suggestion = `Current contrast ratio ${currentRatio.toFixed(2)} is below required ${requiredRatio.toFixed(1)}. `;
    
    if (shortfallPercent > 50) {
        suggestion += "Consider using a much darker or lighter color.";
    } else if (shortfallPercent > 20) {
        suggestion += "Try adjusting the color brightness significantly.";
    } else {
        suggestion += "Small color adjustment should fix this.";
    }
    
    if (context.hasOutline) {
        suggestion += " The outline will help improve readability.";
    }
    
    return suggestion;
}

/**
 * Quick contrast check (simplified for common use cases)
 */
export function quickContrastCheck(foregroundColor: string, backgroundColor: string): boolean {
    const result = validateContrast(foregroundColor, backgroundColor, {}, 'AA_NORMAL');
    return result.sufficientContrast;
}

/**
 * Get best contrast color (black or white)
 */
export function getBestContrastColor(backgroundColor: string): string {
    const bgLuminance = getColorLuminance(backgroundColor);
    const blackContrast = calculateContrastRatio(bgLuminance, 0);
    const whiteContrast = calculateContrastRatio(bgLuminance, 1);
    
    return blackContrast > whiteContrast ? "#000000" : "#FFFFFF";
}

/**
 * Professional color palette with good contrast
 */
export const PROFESSIONAL_COLOR_PALETTES = {
    highContrast: {
        light: ["#FFFFFF", "#F8F9FA", "#E9ECEF", "#DEE2E6"],
        dark: ["#000000", "#212529", "#343A40", "#495057"],
        accent: ["#007BFF", "#28A745", "#DC3545", "#FFC107", "#17A2B8", "#6F42C1"]
    },
    accessible: {
        backgrounds: ["#FFFFFF", "#000000", "#F8F9FA", "#212529"],
        texts: ["#000000", "#FFFFFF", "#343A40", "#F8F9FA"],
        highlights: ["#0066CC", "#228B22", "#CC0000", "#FF8C00"]
    }
} as const;

export default {
    validateContrast,
    quickContrastCheck,
    getBestContrastColor,
    calculateContrastRatio,
    getColorLuminance,
    colorNameToHex,
    CONTRAST_STANDARDS,
    PROFESSIONAL_COLOR_PALETTES
};